import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { Grid, makeStyles, useTheme, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SyncAltIcon from "@material-ui/icons/SyncAlt";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    maxWidth: "700px",
    width: "100%",
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}40` : "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.55)",
    overflow: "hidden",
    "& *:before, & *:after": {
      borderBottom: "none !important",
    },
    "& .MuiInput-underline:before, & .MuiInput-underline:after": {
      display: "none !important",
      borderBottom: "none !important",
      content: "none !important",
    },
    "& .MuiInputBase-root:before, & .MuiInputBase-root:after": {
      display: "none !important",
      borderBottom: "none !important",
      content: "none !important",
    },
    "& .MuiAutocomplete-inputRoot:before, & .MuiAutocomplete-inputRoot:after": {
      display: "none !important",
      borderBottom: "none !important",
      content: "none !important",
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px 12px 24px",
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
  },
  dialogTitleText: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#FFF" : theme.palette.primary.main,
  },
  dialogContent: {
    padding: "20px 24px",
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#fdfdfd",
  },
  dialogActions: {
    padding: "14px 24px",
    borderTop: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#f8fafc",
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
  },
  autocompleteInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.23)",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.4)",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInput-underline:before, & .MuiInput-underline:after": {
      display: "none !important",
      borderBottom: "none !important",
      content: "none !important",
    },
    "&:before, &:after": {
      display: "none !important",
      borderBottom: "none !important",
      content: "none !important",
    },
  },
}));

const filterOptions = createFilterOptions({
  trim: true,
});

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid, ticket }) => {
  const history = useHistory();
  const theme = useTheme();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const classes = useStyles();
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const [msgTransfer, setMsgTransfer] = useState('');
  const [companySettings, setCompanySettings] = useState({});
  const { user } = useContext(AuthContext);
  const { getAll: getAllSettings } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
  }, []);

  useEffect(() => {
    const fetchCompanySettings = async () => {
      if (!user || !user.companyId) return;
      try {
        const settings = await getAllSettings(user.companyId);
        setCompanySettings(settings);
      } catch (err) {
        toastError(err);
      }
    };
    fetchCompanySettings();
  }, [user]);

  useEffect(() => {
    if (!modalOpen) return;
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: searchParam ? { searchParam } : undefined,
          });
          setOptions(data.users || []);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, searchParam ? 300 : 0);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleMsgTransferChange = (event) => {
    setMsgTransfer(event.target.value);
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
  };

  const handleSaveTicket = async (e) => {
    if (!ticketid) return;
    if (!selectedQueue || selectedQueue === "") return;
    setLoading(true);
    try {
      let data = {};
      data.userId = !selectedUser ? null : selectedUser.id;
      
      const transferStatus = companySettings.transferredTicketStatus || "open";
      data.status = !selectedUser ? "pending" : ticket.isGroup ? "group" : transferStatus;
      
      data.queueId = selectedQueue;
      data.msgTransfer = msgTransfer ? msgTransfer : null;
      data.isTransfered = true;

      await api.put(`/tickets/${ticketid}`, data);
      setLoading(false);
      history.push(`/tickets/`);
      handleClose();
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div className={classes.dialogTitleText}>
          <SyncAltIcon style={{ color: theme.palette.primary.main }} />
          <span>{i18n.t("transferTicketModal.title")}</span>
        </div>
        <IconButton size="small" onClick={handleClose} style={{ color: "rgba(255, 255, 255, 0.6)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent} dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} xl={6}>
            <Autocomplete
              openOnFocus
              fullWidth
              size="small"
              getOptionLabel={(option) => `${option.name}`}
              onChange={(e, newValue) => {
                setSelectedUser(newValue);
                if (newValue != null && Array.isArray(newValue.queues)) {
                  if (newValue.queues.length === 1) {
                    setSelectedQueue(newValue.queues[0].id);
                  }
                  setQueues(newValue.queues);
                } else {
                  setQueues(allQueues);
                  setSelectedQueue("");
                }
              }}
              options={options}
              filterOptions={filterOptions}
              noOptionsText={i18n.t("transferTicketModal.noOptions")}
              loading={loading}
              renderOption={option => (<span> <UserStatusIcon user={option} /> {option.name}</span>)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  className={classes.autocompleteInput}
                  label={i18n.t("transferTicketModal.fieldLabel")}
                  variant="outlined"
                  onChange={(e) => setSearchParam(e.target.value)}
                />
              )}
            />
          </Grid>
          <Grid xs={12} sm={6} xl={6} item >
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>
                {i18n.t("transferTicketModal.fieldQueueLabel")}
              </InputLabel>
              <Select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value)}
                label={i18n.t("transferTicketModal.fieldQueueLabel")}
              >
                {queues.map((queue) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={12} sm={12} xl={12} >
            <TextField
              label={i18n.t("transferTicketModal.msgTransfer")}
              value={msgTransfer}
              onChange={handleMsgTransferChange}
              variant="outlined"
              multiline
              maxRows={5}
              minRows={4}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          size="small"
          className={classes.dangerButton}
          style={{ textTransform: "capitalize" }}
          startIcon={<CloseIcon style={{ fontSize: 18 }} />}
        >
          {i18n.t("transferTicketModal.buttons.cancel")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          className={classes.applyButton}
          style={{ textTransform: "capitalize" }}
          disabled={loading || selectedQueue === ""}
          onClick={() => handleSaveTicket(selectedQueue)}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SyncAltIcon style={{ fontSize: 18 }} />}
        >
          {i18n.t("transferTicketModal.buttons.ok")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferTicketModalCustom;
